import { inject, Injectable } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { AuthService, DismissedPopupKeywords, LocalStorage } from '@isophi/core-legacy';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { BehaviorSubject, filter, map, Observable, take } from 'rxjs';

import { UserService } from '../../core/services/user.service';
import { ChildrenPortfolioPopupComponent } from '../../modules/children-portfolio/components/children-portfolio-popup/children-portfolio-popup.component';
import { ManagementPopupComponent } from '../../modules/dashboard/components/management-popup/management-popup.component';
import { PapPopupComponent } from '../../modules/pap/components/pap-popup/pap-popup.component';
import { SettingsPopupComponent } from '../../modules/settings/components/settings-popup/settings-popup.component';
import { DemoPopupComponent } from '../components/demo-popup/demo-popup.component';

export interface ManagePopupReturn {
  allow: boolean;
  url: string;
}

@Injectable({
  providedIn: 'root',
})
export class PopupService {
  private readonly modalService = inject(NgbModal);
  private readonly authService = inject(AuthService);
  private readonly userService = inject(UserService);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly router = inject(Router);

  private readonly localStorage = new LocalStorage();

  private readonly openedPopups: Partial<Record<DismissedPopupKeywords, NgbModalRef>> = {};
  private readonly anyPopupOpenSubject = new BehaviorSubject<boolean>(false);

  readonly anyPopupOpen$ = this.anyPopupOpenSubject.asObservable();

  tryShowPopup(popupKeyword: DismissedPopupKeywords) {
    this.authService.loggedUser$
      .pipe(
        filter((user) => !!user),
        take(1)
      )
      .subscribe((user) => {
        // Check if opened
        if (user.teacher.dismissedPopupKeywords.some((dismissed) => dismissed === popupKeyword) || !!this.openedPopups[popupKeyword]) {
          return;
        }

        const demoKey = `${'m_app_demo' as DismissedPopupKeywords}-${this.activatedRoute.snapshot.queryParams['originUserUuid']}`;

        // Check if demo popup should be shown
        // Show only when going from 'normal' account to demo, not when going from demo to demo. That is handled by query param
        if (
          this.userService.loggedKindergarten.isDemo &&
          popupKeyword === 'm_app_demo' &&
          this.activatedRoute.snapshot.queryParams['showDemoPopup'] &&
          !this.localStorage.getItem(demoKey)
        ) {
          const modalRef = this.modalService.open(DemoPopupComponent);
          this.openedPopups[popupKeyword] = modalRef;
          modalRef.componentInstance.popupKey = popupKeyword;
          modalRef.componentInstance.localKey = demoKey;
          this.trackPopupLifecycle(popupKeyword, modalRef);
          return;
        }

        // all popups (except demo) should not be displayed in demo kindergarten
        if (this.userService.loggedKindergarten.isDemo) {
          return;
        }

        switch (popupKeyword) {
          case 'm_app_children_portfolio':
            {
              const modalRef = this.modalService.open(ChildrenPortfolioPopupComponent);
              this.openedPopups[popupKeyword] = modalRef;
              modalRef.componentInstance.popupKey = popupKeyword;
              this.trackPopupLifecycle(popupKeyword, modalRef);
            }
            return;
          case 'm_app_management':
            {
              const modalRef = this.modalService.open(ManagementPopupComponent);
              this.openedPopups[popupKeyword] = modalRef;
              modalRef.componentInstance.popupKey = popupKeyword;
              this.trackPopupLifecycle(popupKeyword, modalRef);
            }
            return;
          case 'm_app_pap':
            {
              const modalRef = this.modalService.open(PapPopupComponent);
              this.openedPopups[popupKeyword] = modalRef;
              modalRef.componentInstance.popupKey = popupKeyword;
              this.trackPopupLifecycle(popupKeyword, modalRef);
            }
            return;
          case 'm_app_settings':
            {
              const modalRef = this.modalService.open(SettingsPopupComponent);
              this.openedPopups[popupKeyword] = modalRef;
              modalRef.componentInstance.popupKey = popupKeyword;
              this.trackPopupLifecycle(popupKeyword, modalRef);
            }
            return;
        }
      });
  }

  /** Dismiss popup from ever showing again */
  dismissPopup(popUpKey: DismissedPopupKeywords) {
    this.userService.dismissTeacherPopup(popUpKey);
  }

  closePopup(popUpKey: DismissedPopupKeywords, onDestroy = false) {
    const previouslyOpened = this.openedPopups[popUpKey];
    if (previouslyOpened) {
      if (!onDestroy) {
        previouslyOpened.close();
      }
    }
    delete this.openedPopups[popUpKey];
    this.updatePopupOpenState();
  }

  isAnyPopupOpen(): boolean {
    return Object.keys(this.openedPopups).length > 0;
  }

  private trackPopupLifecycle(popupKey: DismissedPopupKeywords, modalRef: NgbModalRef): void {
    this.updatePopupOpenState();
    modalRef.result.finally(() => {
      if (this.openedPopups[popupKey]) {
        delete this.openedPopups[popupKey];
      }
      this.updatePopupOpenState();
    });
  }

  private updatePopupOpenState(): void {
    this.anyPopupOpenSubject.next(Object.keys(this.openedPopups).length > 0);
  }

  /**
   * Manages when popup can be requested to be shown in certain module.
   * @param moduleUrl module root url that will be tested
   * @returns if request for popup can be made
   */
  manageModulePopup(moduleUrl: string): Observable<ManagePopupReturn> {
    let allow = true;

    return this.router.events.pipe(
      filter((event) => event instanceof NavigationEnd),
      map((event: NavigationEnd) => {
        const urlContains = event.url.includes(moduleUrl);
        const baseObj: ManagePopupReturn = { url: event.url, allow: false };
        if (allow && urlContains) {
          allow = false;

          baseObj.allow = true;
          return baseObj;
        }

        if (!urlContains && !allow) {
          allow = true;
        }
        return baseObj;
      })
    );
  }
}
